/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
    FileText,
    HelpCircle,
    X,
    ChevronDown,
    ShieldAlert,
    BookOpen,
    Globe,
    Scale,
    Coins,
    Users,
    Lock,
    ChevronLeft,
    ChevronRight,
    Check,
    Mail
} from 'lucide-react';
import { TRANSLATIONS, LanguageCode } from '../translations';

const TUTORIAL_DATA: Record<string, {
    title: string;
    subtitle: string;
    steps: {
        title: string;
        desc: string;
        details: string;
        badge: string;
        features: string[];
        bgGradient: string;
    }[];
    backBtn: string;
    nextBtn: string;
    finishBtn: string;
}> = {
    en: {
        title: "STICKER ALBUM TUTORIAL",
        subtitle: "HOW TO BECOME A MASTER COLLECTOR",
        backBtn: "Back",
        nextBtn: "Next Step",
        finishBtn: "Get Started!",
        steps: [
            {
                title: "🏟️ Unbox and Glue Your Stickers",
                desc: "Open daily packs to collect dynamic players representing all 32 participating squads.",
                details: "Tiers range from Common stickers to rare Holos/Golds of superstar icons. Tap each card in your pack to reveal it, then add them to your collection! Once complete, a squad earns a verified trophy seal.",
                badge: "PACK OPENER SIMULATOR",
                features: ["2 Free Packs Daily", "Holographic Superstar Drops", "Milestone Progress Rewards"],
                bgGradient: "from-blue-600/10 to-indigo-600/5",
            },
            {
                title: "🤝 Trade Spares & Toggle Beacons",
                desc: "Got duplicate stickers? Swap them easily or gift spares to your friends.",
                details: "Double/Triple collectibles are stored in your Spare vault. Toggle 'Beacon Active' on your profile to make duplicates visible to other collectors. Friends can send letters, accept swaps 1:1, or mail gifts directly to bypass limits.",
                badge: "COMMUNITY COOPERATIVE BOARD",
                features: ["Direct 1-to-1 Swaps", "Mailbox Gift Exchanges", "Beacon Discovery Mode"],
                bgGradient: "from-emerald-600/10 to-teal-600/5",
            },
            {
                title: "🏆 Football Prediction Pools",
                desc: "Check real standings, inspect upcoming matches, and submit predictions.",
                details: "Prove your knowledge on the Schedules and Groups tabs. Enter match forecast scores, view aggregate stand lists, find stadiums, and filter fixtures by calendar date or confederation to optimize your tracking.",
                badge: "WC 2026 BRACKET SCHEDULER",
                features: ["Live Group Standings", "Match Date Filtering", "Predict & Lock Scores"],
                bgGradient: "from-amber-600/10 to-orange-600/5",
            },
            {
                title: "💾 Secure Your Album on Cloud",
                desc: "Never lose your superstar roster. Register or link with GitHub.",
                details: "Guest progress is saved inside browser LocalStorage and resets if cache files are cleared. Register with free credentials to secure your items on Google cloud Firestore database, or export/restore secure payloads using GitHub Gists!",
                badge: "LEDGER ENCRYPTION HANDSHAKE",
                features: ["Firebase Cloud Storage", "GitHub Gist Ledger Backups", "Multi-device Profile Sync"],
                bgGradient: "from-purple-600/10 to-fuchsia-600/5",
            }
        ]
    },
    es: {
        title: "TUTORIAL DE ÁLBUM COLECTIVO",
        subtitle: "CÓMO CONVERTIRSE EN UN SÚPER RECOLECTOR",
        backBtn: "Atrás",
        nextBtn: "Siguiente Paso",
        finishBtn: "¡Empezar a Jugar!",
        steps: [
            {
                title: "🏟️ Abre Sobres y Pega Estampas",
                desc: "Abre paquetes diarios para coleccionar jugadores de las 32 selecciones participantes.",
                details: "Las categorías van desde comunes hasta cartas holográficas doradas de superestrellas mundiales. ¡Toca cada estampita para revelarla y agrégala a tu álbum! Cada selección completada añade un trofeo dorado.",
                badge: "SIMULACIÓN DE APERTURA",
                features: ["2 Sobres Gratis Diarios", "Estampas Holográficas Doradas", "Premios según Hitos"],
                bgGradient: "from-blue-600/10 to-indigo-600/5",
            },
            {
                title: "🤝 Cambia Repetidas y Activa tu Faro",
                desc: "¿Tienes estampas repetidas? Intercámbialas o regálalas a tus amigos.",
                details: "Las estampas adicionales se guardan en tu inventario de Repetidas. Activa el 'Faro Amistoso' para ser visible en el Trade Board. Tus conocidos podrán enviarte cartas directas, ofertas de intercambio equitativos o regalos directos.",
                badge: "INTERCAMBIOS DE LA COMUNIDAD",
                features: ["Intercambios Justos 1:1", "Regalos a Buzón del Amigo", "Descubrimiento de Coleccionistas"],
                bgGradient: "from-emerald-600/10 to-teal-600/5",
            },
            {
                title: "🏆 Quinielas y Pronósticos de Fútbol",
                desc: "Consulta tablas del Mundial, partidos y realiza tus predicciones.",
                details: "Pon a prueba tu instinto en las pestañas 'Schedules' y 'Groups'. Ingresa marcadores simulados, filtra partidos por fecha o confederación, y sigue el standing de grupos de la Copa Mundial 2026 de manera interactiva.",
                badge: "CALENDARIO OFICIAL Y GRUPOS",
                features: ["Standing de Grupos en Vivo", "Filtros por Fecha y Confederación", "Pronósticos Guardados"],
                bgGradient: "from-amber-600/10 to-orange-600/5",
            },
            {
                title: "💾 Guarda tu Álbum en la Nube",
                desc: "No pierdas nunca tus estampas. Regístrate o usa sincronización de GitHub.",
                details: "La sesión de Invitado se almacena localmente y se borra si limpias el navegador. ¡Regístrate gratis para asegurar tu álbum en Firebase Cloud Firestore o haz respaldos encriptados usando tus GitHub Gists!",
                badge: "SEGURIDAD DE RESPALDOS",
                features: ["Cloud Firestore Integrado", "Respaldos con GitHub Gists", "Sincronización Multidispositivo"],
                bgGradient: "from-purple-600/10 to-fuchsia-600/5",
            }
        ]
    },
    bs: {
        title: "VODIČ ZA PAGANINI SAKUPLJANJE",
        subtitle: "KAKO POSTATI MAJSTOR SAKUPLJAČ SLIČICA",
        backBtn: "Nazad",
        nextBtn: "Sljedeći Korak",
        finishBtn: "Započni Igru!",
        steps: [
            {
                title: "🏟️ Otvaranje Paketića i Lijepljenje",
                desc: "Otvaraj besplatne dnevne paketiće i sakupljaj igrače iz sve 32 reprezentacije.",
                details: "Sadrže obične, rijetke i legendarne zlatne zvijezde. Klikni na svaku sličicu da je otkriješ, pa je zalijepi u album! Kompletiranjem reprezentacije osvajaš trofej.",
                badge: "SIMULATOR OTVARANJA",
                features: ["2 Besplatna Paketića Dnevno", "Holografske Zlatne Sličice", "Nagrade za Napredak"],
                bgGradient: "from-blue-600/10 to-indigo-600/5",
            },
            {
                title: "🤝 Razmjena Duplih i Aktivni Svjetionik",
                desc: "Imate duplikate sličica? Razmijenite ih sa prijateljima ili pošaljite poklon.",
                details: "Sve vaše duple sličice se čuvaju u posebnom sefu. Aktivacijom 'Aktivnog Svjetionika' na profilu, vaši duplikati postaju vidljivi na javnoj tabli za razmjenu. Prijatelji mogu razmjenjivati sličice 1:1 ili slati poklone direktno u sandučić.",
                badge: "TABLA ZA RAZMJENU",
                features: ["Poštena 1:1 Razmjena Sličica", "Pokloni Direktno u Sandučić", "Pronalazak Kolekcionara"],
                bgGradient: "from-emerald-600/10 to-teal-600/5",
            },
            {
                title: "🏆 Predviđanje Grupa i Utakmica",
                desc: "Pregledaj tabele, raspored utakmica i unesi svoje prognoze rezultata.",
                details: "Dokaži svoje fudbalsko znanje u sekcijama 'Schedules' i 'Groups'. Filteri ti omogućavaju pretragu po datumu ili konfederaciji. Unesi prognoze, prati tabele uživo i provjeri lokacije stadiona.",
                badge: "RASPORED UTAKMICA & PREDVIĐANJA",
                features: ["Tabele Grupa Uživo", "Filter Pretrage Po Datumu", "Prognoze Rezultata"],
                bgGradient: "from-amber-600/10 to-orange-600/5",
            },
            {
                title: "💾 Sigurno Čuvanje u Oblaku",
                desc: "Nikada nemoj izgubiti svoj napredak. Registruj se ili poveži GitHub.",
                details: "Gostujući profil čuva sličice samo lokalno u pretraživaču. Registruj se besplatno i čuvaj kolekciju zauvijek na Firebase Firestore bazi, ili koristi naprednu integraciju sa GitHub Gist za izvoz i uvoz podataka!",
                badge: "SIGURNOST I BACKUP",
                features: ["Firebase Firestore Baza podataka", "GitHub Gist Backup", "Sinhronizacija na Više Uređaja"],
                bgGradient: "from-purple-600/10 to-fuchsia-600/5",
            }
        ]
    }
};

interface FooterProps {
    lang?: string;
}

export default function Footer({ lang = 'en' }: FooterProps) {
    const [activeModal, setActiveModal] = useState<'none' | 'terms' | 'faq' | 'tutorial'>('none');
    const [faqCategory, setFaqCategory] = useState<'all' | 'Gameplay' | 'Trading' | 'Accounts' | 'General'>('all');
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const [tutorialStep, setTutorialStep] = useState<number>(0);

    // Safely retrieve translations with smart regional fallbacks (e.g. Serbian/Croatian fallback to Bosnian)
    const getTranslationValue = (key: string, fallbackLang: LanguageCode = 'en') => {
        const targetDict = TRANSLATIONS[lang as LanguageCode];
        if (targetDict && targetDict[key] !== undefined) {
            return targetDict[key];
        }

        // Smart regional fallback: Bosnian is fully and beautifully translated
        if (lang === 'hr' || lang === 'sr') {
            const bsDict = TRANSLATIONS['bs'];
            if (bsDict && bsDict[key] !== undefined) {
                return bsDict[key];
            }
        }

        const englishDict = TRANSLATIONS[fallbackLang];
        return englishDict?.[key];
    };

    const brandDesc = getTranslationValue('footer_brandDesc');
    const commonQuestions = getTranslationValue('footer_commonQuestions');
    const termsOfUse = getTranslationValue('footer_termsOfUse');
    const simulationOnly = getTranslationValue('footer_simulationOnly');
    const faqTitle = getTranslationValue('footer_faqTitle');
    const faqSubtitle = getTranslationValue('footer_faqSubtitle');
    const allFields = getTranslationValue('footer_allFields');
    const aiBotNote = getTranslationValue('footer_aiBotNote');
    const termsTitle = getTranslationValue('footer_termsTitle');
    const termsSubtitle = getTranslationValue('footer_termsSubtitle');
    const acceptTerms = getTranslationValue('footer_acceptTerms');
    const categories = getTranslationValue('footer_categories') || {
        Gameplay: 'Gameplay',
        Trading: 'Trading',
        Accounts: 'Accounts',
        General: 'General'
    };
    const faqs = getTranslationValue('footer_faqs') || [];
    const terms = getTranslationValue('footer_terms') || [];

    const t = {
        brandDesc,
        commonQuestions,
        termsOfUse,
        simulationOnly,
        faqTitle,
        faqSubtitle,
        allFields,
        aiBotNote,
        termsTitle,
        termsSubtitle,
        acceptTerms,
        categories,
        faqs,
        terms
    };

    const getTutorialDict = () => {
        const activeLang: string = lang || 'en';
        if (activeLang === 'hr' || activeLang === 'sr' || activeLang === 'bs') {
            return TUTORIAL_DATA['bs'];
        }
        if (activeLang === 'es') {
            return TUTORIAL_DATA['es'];
        }
        return TUTORIAL_DATA['en'];
    };
    const activeTutorial = getTutorialDict();
    const currentStep = activeTutorial.steps[tutorialStep] || activeTutorial.steps[0];

    // Close modal on escape key press
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveModal('none');
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const getIcon = (iconName: string) => {
        switch (iconName) {
            case 'book': return <BookOpen size={13} className="text-emerald-400" />;
            case 'coins': return <Coins size={13} className="text-emerald-400 font-bold" />;
            case 'users': return <Users size={13} className="text-emerald-400 font-bold" />;
            case 'lock': return <Lock size={13} className="text-emerald-400 font-bold" />;
            case 'alert': return <ShieldAlert size={13} className="text-emerald-400 font-bold" />;
            case 'globe': return <Globe size={13} className="text-emerald-400 font-bold" />;
            default: return <BookOpen size={13} className="text-emerald-400" />;
        }
    };

    const filteredFaqs = faqCategory === 'all'
        ? t.faqs
        : t.faqs.filter(f => f.category === faqCategory);

    return (
        <>
            {/* Footer Area */}
            <footer className="w-full bg-[#090d16] border-t border-slate-900 py-8 px-6 mt-16 text-center select-none pin-b relative z-30">
                <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    {/* Brand Column */}
                    <div className="text-left md:max-w-xs">
                        <h4 className="font-impact text-md tracking-tighter text-white uppercase leading-none">
                            PAGANINI <span className="text-blue-500">2026</span>
                        </h4>
                        <p className="text-[10px] text-slate-500 font-mono mt-1.5 leading-relaxed">
                            {t.brandDesc}
                        </p>
                    </div>

                    {/* Navigation Links Column */}
                    <div className="flex flex-wrap justify-center gap-6">
                        <button
                            onClick={() => { setActiveModal('tutorial'); setTutorialStep(0); }}
                            className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-400 hover:text-amber-400 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                            <BookOpen size={14} className="text-amber-500" />
                            <span>{lang === 'es' ? 'Cómo Jugar / Tutorial' : lang === 'bs' || lang === 'hr' || lang === 'sr' ? 'Uputstvo / Vodič' : 'How To Play / Tutorial'}</span>
                        </button>
                        <button
                            onClick={() => { setActiveModal('faq'); setOpenFaqIndex(null); }}
                            className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-400 hover:text-blue-400 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                            <HelpCircle size={14} className="text-blue-500" />
                            <span>{t.commonQuestions}</span>
                        </button>
                        <button
                            onClick={() => setActiveModal('terms')}
                            className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-400 hover:text-emerald-400 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                            <FileText size={14} className="text-emerald-500" />
                            <span>{t.termsOfUse}</span>
                        </button>
                        <a
                            href="mailto:coffeebreak.interactive@gmail.com"
                            className="flex items-center gap-1.5 font-mono text-[11px] font-bold text-slate-400 hover:text-rose-400 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                            <Mail size={14} className="text-rose-500" />
                            <span>{lang === 'es' ? 'Contacto' : lang === 'bs' || lang === 'hr' || lang === 'sr' ? 'Kontakt' : 'Contact'}</span>
                        </a>
                    </div>

                    {/* Copyright/Disclaimers Column */}
                    <div className="text-right text-[10px] font-mono text-slate-600">
                        <p>© 2026 PAGANINI TEAM</p>
                        <p className="text-[8px] tracking-wide uppercase mt-1 text-slate-600">
                            {t.simulationOnly}
                        </p>
                    </div>
                </div>
            </footer>

            {/* Interactive FAQ Modal */}
            <AnimatePresence>
                {activeModal === 'faq' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md"
                        onClick={() => setActiveModal('none')}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 15 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 15 }}
                            className="bg-[#0f172a] border border-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative flex flex-col max-h-[85vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-800 shrink-0 bg-slate-950/40">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                            <HelpCircle size={18} className="text-blue-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-impact text-lg uppercase tracking-tight text-white">{t.faqTitle}</h3>
                                            <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">{t.faqSubtitle}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setActiveModal('none')}
                                        className="text-slate-500 hover:text-white bg-slate-800/40 hover:bg-slate-800/80 rounded-full p-2 transition-colors cursor-pointer"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>

                                {/* Categories filtering bar */}
                                <div className="flex flex-wrap gap-1.5 mt-5">
                                    {(['all', 'Gameplay', 'Trading', 'Accounts', 'General'] as const).map((cat) => {
                                        const label = cat === 'all' ? t.allFields : t.categories[cat];
                                        return (
                                            <button
                                                key={cat}
                                                onClick={() => { setFaqCategory(cat); setOpenFaqIndex(null); }}
                                                className={`px-3 py-1.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider transition-all cursor-pointer ${
                                                    faqCategory === cat
                                                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/10'
                                                        : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border border-slate-800'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Scrollable Questions Wrapper */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-3.5 custom-scrollbar bg-slate-900/10 mb-2">
                                {filteredFaqs.length > 0 ? (
                                    filteredFaqs.map((faq, index) => {
                                        const isOpen = openFaqIndex === index;
                                        return (
                                            <div
                                                key={index}
                                                className={`border rounded-xl transition-all overflow-hidden ${
                                                    isOpen
                                                        ? 'bg-slate-950/60 border-blue-500/30'
                                                        : 'bg-slate-900/40 border-slate-800/80 hover:border-slate-800'
                                                }`}
                                            >
                                                <button
                                                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                                                    className="w-full py-4 px-4 flex items-center justify-between text-left cursor-pointer transition-colors hover:bg-slate-950/10"
                                                >
                                                    <div className="flex items-center gap-3 pr-4">
                                                        <span className="text-base select-none">{faq.icon}</span>
                                                        <span className="text-xs font-bold text-slate-200 uppercase tracking-tight">{faq.q}</span>
                                                    </div>
                                                    <ChevronDown
                                                        size={16}
                                                        className={`text-slate-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-blue-400' : ''}`}
                                                    />
                                                </button>

                                                <AnimatePresence initial={false}>
                                                    {isOpen && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.2 }}
                                                            className="border-t border-slate-900"
                                                        >
                                                            <div className="p-4 bg-slate-950/30 text-[11px] text-slate-400 leading-relaxed space-y-2">
                                                                <p className="font-sans">{faq.a}</p>
                                                                <div className="flex items-center gap-1 text-[8px] font-mono text-slate-500 select-none">
                                                                    <span>TAG:</span>
                                                                    <span className="bg-slate-800 text-slate-400 px-1 py-0.5 rounded font-bold uppercase tracking-wider">{t.categories[faq.category]}</span>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center py-12 text-slate-500 font-mono text-xs uppercase tracking-wider">
                                        No FAQs matching query index found.
                                    </div>
                                )}
                            </div>

                            {/* Help footer note */}
                            <div className="p-4 border-t border-slate-800 shrink-0 text-center bg-slate-950/20 text-[9px] font-mono text-slate-500 uppercase tracking-widest select-none">
                                {t.aiBotNote}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Interactive Terms of Use Modal */}
            <AnimatePresence>
                {activeModal === 'terms' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md"
                        onClick={() => setActiveModal('none')}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 15 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 15 }}
                            className="bg-[#0f172a] border border-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative flex flex-col max-h-[85vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-800 shrink-0 bg-slate-950/40">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                            <Scale size={18} className="text-emerald-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-impact text-lg uppercase tracking-tight text-white">{t.termsTitle}</h3>
                                            <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">{t.termsSubtitle}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setActiveModal('none')}
                                        className="text-slate-500 hover:text-white bg-slate-800/40 hover:bg-slate-800/80 rounded-full p-2 transition-colors cursor-pointer"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable Content Wrapper */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar text-left text-[11px] leading-relaxed">
                                {t.terms.map((clause, idx) => (
                                    <div key={idx} className={idx === 0 ? "bg-slate-950/40 border border-slate-900 rounded-xl p-4 space-y-1.5" : "space-y-1.5"}>
                                        <h4 className="font-bold text-slate-200 uppercase tracking-tight flex items-center gap-1.5 font-mono text-xs">
                                            {getIcon(clause.iconName)}
                                            <span>{clause.title}</span>
                                        </h4>
                                        <p className="font-sans text-slate-400">
                                            {clause.body}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Close Action Buttons Footer */}
                            <div className="p-4 border-t border-slate-800 shrink-0 bg-slate-950/40 flex justify-end gap-2 text-right">
                                <button
                                    onClick={() => setActiveModal('none')}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-mono font-black text-[10px] rounded uppercase tracking-wider transition-all duration-150 cursor-pointer active:scale-95 shadow-md shadow-emerald-500/10"
                                >
                                    {t.acceptTerms}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Interactive Onboarding Tutorial Modal */}
            <AnimatePresence>
                {activeModal === 'tutorial' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md"
                        onClick={() => setActiveModal('none')}
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 15 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 15 }}
                            className="bg-[#0f172a] border border-slate-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl relative flex flex-col max-h-[85vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="p-6 border-b border-slate-800 shrink-0 bg-slate-950/40">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                                            <BookOpen size={18} className="text-amber-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-impact text-lg uppercase tracking-tight text-white">{activeTutorial.title}</h3>
                                            <p className="text-[9px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">{activeTutorial.subtitle}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setActiveModal('none')}
                                        className="text-slate-500 hover:text-white bg-slate-800/40 hover:bg-slate-800/80 rounded-full p-2 transition-colors cursor-pointer"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>

                                {/* Steps Horizontal Progress Indicator */}
                                <div className="flex justify-between items-center mt-5">
                                    <div className="flex items-center gap-2">
                                        {activeTutorial.steps.map((_, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setTutorialStep(idx)}
                                                className={`h-2 rounded-full transition-all duration-300 ${
                                                    tutorialStep === idx
                                                        ? 'w-8 bg-amber-500 shadow-md shadow-amber-500/25'
                                                        : 'w-2 bg-slate-800 hover:bg-slate-700'
                                                }`}
                                                title={`Go to step ${idx + 1}`}
                                            />
                                        ))}
                                    </div>
                                    <div className="text-[9px] font-mono text-slate-500 uppercase tracking-widest font-black">
                                        {lang === 'es' ? 'PASO' : lang === 'bs' || lang === 'hr' || lang === 'sr' ? 'KORAK' : 'STEP'} {tutorialStep + 1} {lang === 'es' ? 'DE' : lang === 'bs' || lang === 'hr' || lang === 'sr' ? 'OD' : 'OF'} {activeTutorial.steps.length}
                                    </div>
                                </div>
                            </div>

                            {/* Animated Content Space */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar bg-slate-900/10">
                                <motion.div
                                    key={tutorialStep}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.2 }}
                                    className={`border border-slate-800/50 rounded-2xl p-6 bg-gradient-to-b ${currentStep.bgGradient} space-y-4 shadow-xl`}
                                >
                                    <div className="flex items-center justify-between">
                    <span className="text-[9px] font-mono bg-amber-500/25 border border-amber-500/40 text-amber-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                      {currentStep.badge}
                    </span>
                                        <span className="text-xs font-mono text-slate-500">
                      🏟️ PAGANINI 2026
                    </span>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-impact text-lg uppercase leading-tight tracking-tight text-white">
                                            {currentStep.title}
                                        </h4>
                                        <p className="text-slate-300 font-sans text-xs leading-relaxed font-semibold">
                                            {currentStep.desc}
                                        </p>
                                        <p className="text-slate-400 font-sans text-xs leading-relaxed bg-slate-950/25 border border-slate-900/80 rounded-xl p-4.5">
                                            {currentStep.details}
                                        </p>
                                    </div>

                                    {/* Bullet Highlights */}
                                    <div className="pt-2 border-t border-slate-900 grid grid-cols-1 md:grid-cols-3 gap-2">
                                        {currentStep.features.map((feat, idx) => (
                                            <div key={idx} className="bg-slate-950/40 p-2.5 rounded-xl border border-slate-900 flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                                                    <Check size={10} className="text-emerald-400 font-bold" />
                                                </div>
                                                <span className="text-[9px] font-mono text-slate-300 font-bold uppercase tracking-tight">{feat}</span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </div>

                            {/* Modal Navigation Buttons Footer */}
                            <div className="p-4 border-t border-slate-800 shrink-0 bg-slate-950/40 flex justify-between items-center">
                                <button
                                    onClick={() => setTutorialStep(prev => Math.max(0, prev - 1))}
                                    disabled={tutorialStep === 0}
                                    className={`px-4 py-2 font-mono font-bold text-[9px] rounded uppercase tracking-wider transition-all duration-150 flex items-center gap-1.5 ${
                                        tutorialStep === 0
                                            ? 'text-slate-600 cursor-not-allowed border border-slate-900 bg-slate-950/10'
                                            : 'bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                                    }`}
                                >
                                    <ChevronLeft size={12} />
                                    <span>{activeTutorial.backBtn}</span>
                                </button>

                                {tutorialStep < activeTutorial.steps.length - 1 ? (
                                    <button
                                        onClick={() => setTutorialStep(prev => prev + 1)}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-slate-950 font-mono font-black text-[9px] rounded uppercase tracking-wider transition-all duration-150 flex items-center gap-1.5 cursor-pointer shadow-md shadow-blue-500/10 active:scale-95"
                                    >
                                        <span>{activeTutorial.nextBtn}</span>
                                        <ChevronRight size={12} />
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setActiveModal('none')}
                                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-mono font-black text-[9px] rounded uppercase tracking-wider transition-all duration-150 flex items-center gap-1.5 cursor-pointer shadow-md shadow-emerald-500/10 active:scale-95"
                                    >
                                        <span>{activeTutorial.finishBtn}</span>
                                        <Check size={12} />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
